import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Anchor, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../App';

const ReferralPage = () => {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const validateRef = async () => {
      if (!refCode) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/referrals/validate/${refCode}`);
        if (response.data.valid) {
          setPartner(response.data);
          setValid(true);
          // Store referral code in session for booking
          sessionStorage.setItem('referral_code', refCode);
        }
      } catch (error) {
        console.error('Error validating referral:', error);
      } finally {
        setLoading(false);
      }
    };
    validateRef();
  }, [refCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1580503013542-099c082f1c00?w=1920&q=80')`
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-6">
            <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-16 w-auto" />
          </div>
          
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
            Welcome to Paradise!
          </h1>
          
          {valid && partner && (
            <div className="glass rounded-xl p-4 mb-6 inline-block">
              <div className="flex items-center gap-2 text-foreground">
                <Check className="w-5 h-5 text-green-600" />
                <span>Referred by <strong>{partner.partner_name}</strong></span>
              </div>
            </div>
          )}
          
          <p className="text-lg text-white/90 mb-8">
            Discover unforgettable tours, boat adventures, and island experiences in Nassau, Bahamas.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold mb-4">
              Ready for Your Adventure?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Browse our tour and experience, select your preferred date and time, and book with just a 50% deposit. Pay the balance later!
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-xl border border-border p-6 text-center">
              <div className="w-12 h-12 bg-teal-light rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-primary">1</span>
              </div>
              <h3 className="font-serif font-bold mb-2">Choose Your Tour</h3>
              <p className="text-sm text-muted-foreground">
                Browse boat tours, food experiences, beach adventures, and more.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-border p-6 text-center">
              <div className="w-12 h-12 bg-coral-light rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-coral-dark">2</span>
              </div>
              <h3 className="font-serif font-bold mb-2">Select Date & Time</h3>
              <p className="text-sm text-muted-foreground">
                Pick a time that works for you and add any extras you'd like.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-border p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-green-600">3</span>
              </div>
              <h3 className="font-serif font-bold mb-2">Book & Enjoy!</h3>
              <p className="text-sm text-muted-foreground">
                Pay 50% deposit now, balance later. Show your QR code at check-in.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link to="/tours">
              <Button className="btn-primary text-lg px-8 py-6" data-testid="browse-tours-btn">
                Browse Tours
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            
            {valid && (
              <p className="text-sm text-muted-foreground mt-4">
                Your referral discount will be applied automatically at checkout.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-10 w-auto" />
          </div>
          <p className="text-white/70 text-sm">
            Nassau, Bahamas • hello@thevillageatholisland.com • +1 (242) 825-6265
          </p>
          <p className="text-white/50 text-xs mt-4">
            © 2026 Harbor Point Holdings d/b/a The Village. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ReferralPage;
